import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Grid3X3, ChevronRight, Layers, Users, Stars } from 'lucide-react';
import SEO from '../components/SEO';
import { useCategoriesTree } from '../api/categories';
import { BRAND } from '../config/brand';

function norm(s) {
    return String(s || '').trim().toLowerCase();
}

function flattenTree(tree) {
    const out = [];
    const walk = (node, parents = []) => {
        if (!node) return;
        const nextParents = [...parents, node];
        out.push({ node, parents });
        const kids = Array.isArray(node.children) ? node.children : [];
        kids.forEach((c) => walk(c, nextParents));
    };
    (Array.isArray(tree) ? tree : []).forEach((r) => walk(r, []));
    return out;
}

function getCount(node) {
    // API provides `profileCount` per node (often already aggregated).
    // To avoid double-counting, prefer node.profileCount.
    // Only fallback to child sum when the field is missing.
    if (node?.profileCount === null || node?.profileCount === undefined) {
        const kids = Array.isArray(node?.children) ? node.children : [];
        return kids.reduce((acc, c) => acc + getCount(c), 0);
    }
    return Number(node?.profileCount || 0);
}

function filterTree(nodes, q) {
    const query = norm(q);
    if (!query) return nodes;
    const walk = (n) => {
        const nameMatch = norm(n.name).includes(query);
        const kids = Array.isArray(n.children) ? n.children : [];
        // If node itself matches, keep its full subtree so the UX layout stays intact.
        if (nameMatch) return n;
        const filteredKids = kids.map(walk).filter(Boolean);
        if (filteredKids.length) return { ...n, children: filteredKids };
        return null;
    };
    return (Array.isArray(nodes) ? nodes : []).map(walk).filter(Boolean);
}

const Catalog = () => {
    const [params, setParams] = useSearchParams();
    const q = (params.get('q') || '').trim();

    const { data: categoriesTreeData, isLoading, error } = useCategoriesTree();
    const roots = React.useMemo(() => {
        const t = categoriesTreeData?.tree;
        return Array.isArray(t) ? t : [];
    }, [categoriesTreeData?.tree]);
    const filteredRoots = React.useMemo(() => filterTree(roots, q), [roots, q]);
    const flat = React.useMemo(() => flattenTree(roots), [roots]);

    const totalCategories = flat.length;
    const totalProfiles = React.useMemo(() => {
        // Total profiles in the catalog (avoid double-counting children): sum root counts.
        return (roots || []).reduce((acc, r) => acc + getCount(r), 0);
    }, [roots]);

    const basePath = '/katalog';
    const canonical = React.useMemo(() => {
        if (!q) return `${BRAND.baseUrl}${basePath}`;
        return `${BRAND.baseUrl}${basePath}?q=${encodeURIComponent(q)}`;
    }, [q]);

    const title = 'Каталог категорий';
    const description = 'Полная навигация по категориям: артисты, коллективы, агентства и сервисы. Открывайте разделы и находите профили по жанрам.';

    // Avoid indexing query variants (internal search), keep canonical clean.
    const noindex = !!q;

    const breadcrumbLd = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Главная', item: `${BRAND.baseUrl}/` },
            { '@type': 'ListItem', position: 2, name: 'Каталог', item: canonical }
        ]
    };

    const itemListLd = {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: title,
        description,
        url: canonical,
        inLanguage: 'ru-RU',
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: (roots || []).slice(0, 50).map((c, idx) => ({
                '@type': 'ListItem',
                position: idx + 1,
                url: `${BRAND.baseUrl}/category/${encodeURIComponent(String(c.slug || ''))}`,
                name: String(c.name || '')
            }))
        }
    };

    const structuredData = [breadcrumbLd, itemListLd];

    const onChangeQ = (val) => {
        const next = new URLSearchParams(params);
        if (val) next.set('q', val);
        else next.delete('q');
        setParams(next, { replace: false });
    };

    // IMPORTANT: Keep backend sort order (level ASC, sortOrder ASC, name ASC).
    // Do not resort by profileCount here, otherwise the admin-defined order is lost.

    const renderLevel2Blocks = (root) => {
        const level2 = Array.isArray(root?.children) ? root.children : [];
        if (!level2.length) return null;

        return (
            <div className="mt-4 space-y-6">
                {level2.map((l2) => {
                    const level3 = Array.isArray(l2?.children) ? l2.children : [];
                    const l2Total = getCount(l2);

                    // Edge-case: no level-3 -> render the level-2 as a single link card.
                    if (!level3.length) {
                        return (
                            <div key={l2.id} className="rounded-2xl bg-secondary-50 border border-secondary-100 p-4">
                                <Link
                                    to={`/feed?category=${encodeURIComponent(String(l2.slug || ''))}`}
                                    className="text-sm text-primary-700 hover:underline flex items-center justify-between gap-2"
                                    title={l2.name}
                                >
                                    <span className="truncate font-medium">{l2.name}</span>
                                    <span className="text-xs text-secondary-600">({Number(l2.profileCount || 0)})</span>
                                </Link>

                                {l2?.slug && (
                                    <div className="mt-3">
                                        <Link
                                            to={`/category/${encodeURIComponent(String(l2.slug))}`}
                                            className="inline-flex items-center text-sm font-medium text-secondary-800 hover:text-secondary-900"
                                        >
                                            Открыть страницу категории <ChevronRight className="w-4 h-4 ml-1" />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        );
                    }

                    const sortedLevel3 = level3;

                    return (
                        <div key={l2.id} className="min-w-0">
                            <div className="flex items-baseline justify-between gap-4">
                                <h3 className="text-lg font-semibold text-secondary-900 truncate">{l2.name}</h3>
                                <div className="flex shrink-0 text-xs text-secondary-600 flex"><Users className="w-4 h-4 mr-1 text-primary-600" />{l2Total}</div>
                            </div>

                            <div className="mt-2 rounded-2xl bg-white border border-secondary-100 p-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-x-6 gap-y-2">
                                    {sortedLevel3.map((l3) => (
                                        <Link
                                            key={l3.id}
                                            to={`/feed?category=${encodeURIComponent(String(l3.slug || ''))}`}
                                            className="text-sm text-primary-700 hover:underline flex items-center justify-between gap-2"
                                            title={l3.name}
                                        >
                                            <span className="truncate">{l3.name}</span>
                                            <span className="text-xs text-secondary-600">({Number(l3.profileCount || 0)})</span>
                                        </Link>
                                    ))}
                                </div>

                                {/* Deep link to the category landing (indexable) */}
                                {l2?.slug && (
                                    <div className="mt-4">
                                        <Link
                                            to={`/category/${encodeURIComponent(String(l2.slug))}`}
                                            className="inline-flex items-center text-sm font-medium text-secondary-800 hover:text-secondary-900"
                                        >
                                            Открыть страницу категории <ChevronRight className="w-4 h-4 ml-1" />
                                        </Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}

                {/* Deep link to the level-1 category landing (indexable) */}
                {root?.slug && (
                    <div>
                        <Link
                            to={`/category/${encodeURIComponent(String(root.slug))}`}
                            className="inline-flex items-center text-sm font-medium text-secondary-800 hover:text-secondary-900"
                        >
                            Открыть страницу раздела <ChevronRight className="w-4 h-4 ml-1" />
                        </Link>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <SEO
                title={title}
                description={description}
                url={q ? `${basePath}?q=${encodeURIComponent(q)}` : basePath}
                type="website"
                canonical={canonical}
                structuredData={structuredData}
                noindex={noindex}
            />

            <div className="max-w-7xl mx-auto px-4 py-10">
                {/* Breadcrumbs */}
                <div className="text-sm text-secondary-600">
                    <Link to="/" className="hover:underline">Главная</Link>
                    <span className="mx-2">/</span>
                    <span className="text-secondary-900">Каталог</span>
                </div>

                {/* Header */}
                <div className="mt-3 flex flex-col gap-4">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                            <h1 className="text-3xl md:text-4xl font-bold text-secondary-900 tracking-tight flex items-center gap-2">
                                <Layers className="w-7 h-7 text-primary-600" />
                                <span>Каталог</span>
                            </h1>
                            <p className="mt-2 text-secondary-700 max-w-3xl">
                                Все жанры искусства и развлечений в одном каталоге: артисты, коллективы, агентства и сервисы.
                            </p>
                        </div>

                        <div className="flex items-center gap-2">
                            <span className="text-xs px-3 py-1.5 rounded-full bg-white border border-secondary-200 text-secondary-700">
                                Всего рубрик: <b>{totalCategories}</b>
                            </span>
                            <span className="text-xs px-3 py-1.5 rounded-full bg-white border border-secondary-200 text-secondary-700">
                                Всего арт-профилей: <b>{totalProfiles}</b>
                            </span>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="rounded-2xl bg-white border border-secondary-200 shadow-soft p-4">
                        <label className="block text-sm font-medium text-secondary-900">Быстрый поиск по категориям</label>
                        <div className="mt-2 flex items-center gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-secondary-500" />
                                <input
                                    value={q}
                                    onChange={(e) => onChangeQ(e.target.value)}
                                    placeholder="Например: ведущие, дизайн, танцы…"
                                    className="w-full rounded-xl border border-secondary-200 bg-white pl-10 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                />
                            </div>
                            {q ? (
                                <button
                                    type="button"
                                    onClick={() => onChangeQ('')}
                                    className="shrink-0 px-3 py-2.5 rounded-xl border border-secondary-200 bg-white hover:bg-secondary-50 text-sm"
                                >
                                    Сбросить
                                </button>
                            ) : null}
                        </div>
                        {/* <div className="mt-2 text-xs text-secondary-500">
                            Подсказка: клик по подкатегории ведёт в «Ленту» с фильтром. Страницы /category/* доступны для индексации.
                        </div> */}
                    </div>
                </div>

                {/* Status */}
                {isLoading && (
                    <div className="mt-6 rounded-2xl bg-white ring-1 ring-black/5 shadow-soft p-6">
                        <div className="text-secondary-700">Загрузка категорий…</div>
                    </div>
                )}
                {error && (
                    <div className="mt-6 rounded-2xl bg-white ring-1 ring-red-200 shadow-soft p-6">
                        <div className="text-red-700">Ошибка загрузки категорий</div>
                    </div>
                )}

                {/* Main sections */}
                {!isLoading && !error && (
                    <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {(filteredRoots || []).map((root) => {
                            const sectionTotal = getCount(root);
                            return (
                                <section key={root.id} className="min-w-0">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="min-w-0">
                                            <h2 className="text-2xl font-bold text-secondary-900 flex items-center gap-2">
                                                <Stars className="w-5 h-5 text-primary-600" />
                                                <span className="truncate">{root.name}</span>
                                            </h2>
                                            <p className="mt-1 text-sm text-secondary-600">
                                                Раздел каталога. Выберите подкатегорию или откройте страницу категории.
                                            </p>
                                        </div>
                                        <div className="shrink-0 text-right">
                                            <div className="text-xs text-secondary-500">Всего</div>
                                            <div className="text-lg font-semibold text-secondary-900">{sectionTotal}</div>
                                        </div>
                                    </div>

                                    {renderLevel2Blocks(root)}
                                </section>
                            );
                        })}
                    </div>
                )}

                {/* Empty */}
                {!isLoading && !error && Array.isArray(filteredRoots) && filteredRoots.length === 0 && (
                    <div className="mt-8 rounded-2xl bg-white ring-1 ring-black/5 shadow-soft p-6">
                        <div className="text-secondary-900 font-semibold">Ничего не найдено</div>
                        <div className="mt-1 text-secondary-600">Попробуйте другой запрос или сбросьте фильтр.</div>
                    </div>
                )}
            </div>
        </>
    );
};

export default Catalog;
